import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Building2, Plus } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { AdminPage } from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { formatUSD } from "@/lib/format";

const DEFAULT_FORM = { name: "", slug: "", address: "", city: "", state: "MA", zip: "", phone: "", email: "", website: "", description: "", hours: "" };

const AdminDealers = () => {
  const [rows, setRows] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);

  const reload = () => api.get("/admin/dealers").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { reload(); }, []);

  const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/dealers", { ...form, slug: form.slug || slugify(form.name) });
      toast.success("Dealer created");
      setAddOpen(false);
      setForm(DEFAULT_FORM);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const toggleActive = async (dealer, next) => {
    try {
      await api.patch(`/admin/dealers/${dealer.id}`, { is_active: next });
      toast.success(next ? "Dealer reactivated" : "Dealer deactivated");
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <AdminPage
      title="Partner dealerships"
      subtitle="Manage the dealerships whose inventory shows up on the marketplace."
      testId="admin-dealers-page"
      action={
        <Button className="bg-brand hover:bg-brand-hover text-white" onClick={() => setAddOpen(true)} data-testid="admin-add-dealer-btn">
          <Plus size={14} /> Add Dealer
        </Button>
      }
    >
      {!rows ? (
        <div className="h-64 bg-slate-100 rounded-2xl animate-pulse" />
      ) : rows.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-16 text-center">
          <Building2 size={28} className="mx-auto text-slate-300" />
          <p className="mt-3 text-slate-500 text-sm">No dealers yet.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="admin-dealers-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-widest">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Dealership</th>
                <th className="text-left px-4 py-3 font-semibold">City</th>
                <th className="text-right px-4 py-3 font-semibold">Listings</th>
                <th className="text-right px-4 py-3 font-semibold">Leads 30d</th>
                <th className="text-right px-4 py-3 font-semibold">Showed up 30d</th>
                <th className="text-right px-4 py-3 font-semibold">Billing (MTD)</th>
                <th className="text-center px-4 py-3 font-semibold">Active</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`admin-dealer-row-${d.id}`}>
                  <td className="px-4 py-3">
                    <Link to={`/admin/dealers/${d.id}`} className="text-brand font-medium hover:underline">{d.name}</Link>
                    <p className="text-[11px] text-slate-500">{d.slug}</p>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{d.city}, {d.state}</td>
                  <td className="px-4 py-3 text-right font-medium">{d.active_listings}</td>
                  <td className="px-4 py-3 text-right">{d.total_leads_30d}</td>
                  <td className="px-4 py-3 text-right text-emerald-700 font-medium">{d.showed_up_30d}</td>
                  <td className="px-4 py-3 text-right text-gold font-semibold">{formatUSD(d.estimated_billing_this_month, 0)}</td>
                  <td className="px-4 py-3 text-center">
                    <Switch checked={d.is_active} onCheckedChange={(v) => toggleActive(d, v)} data-testid={`admin-dealer-toggle-${d.id}`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="admin-add-dealer-dialog">
          <DialogHeader>
            <DialogTitle>Add a new dealership</DialogTitle>
            <DialogDescription>Creates the dealer record. You can add dealer user accounts from the dealer detail page.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">Name</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value, slug: form.slug || slugify(e.target.value) })} data-testid="admin-add-dealer-name" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">Slug (URL)</Label>
                <Input required value={form.slug} onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} data-testid="admin-add-dealer-slug" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Address</Label>
              <Input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="admin-add-dealer-address" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">City</Label>
                <Input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="admin-add-dealer-city" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">State</Label>
                <Input required maxLength={2} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">ZIP</Label>
                <Input required value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} data-testid="admin-add-dealer-zip" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">Phone</Label>
                <Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="admin-add-dealer-phone" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">Contact email</Label>
                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-add-dealer-email" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Website</Label>
              <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://..." />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Description</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <DialogFooter>
              <DialogClose asChild><Button type="button" variant="outline">Cancel</Button></DialogClose>
              <Button type="submit" disabled={saving} className="bg-brand hover:bg-brand-hover text-white" data-testid="admin-add-dealer-submit">
                {saving ? "Creating…" : "Create dealer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AdminPage>
  );
};

export default AdminDealers;
